import { lighten, rgba } from "polished";
import { css } from "styled-components";
import { easing } from "ts-easing";

const darkBorderColor = lighten(0.2, "#17171F");

const defaults = {
  spreadTransform: (progress: number, target: number) => {
    const p = easing.inQuad(progress);
    return p * -0.5 * target - 0.5;
  },
  opacityTransform: (progress: number, opacity: number) => {
    const p = 1 - easing.inOutExpo(progress);
    return p * opacity;
  },
  offsetTransform: (progress: number, target: number) => {
    return easing.inQuad(progress) * target + 1;
  },
  blurTransform: (progress: number, target: number) => {
    const p = easing.inQuad(progress);
    return p * target * 1.0625 + 1;
  },
};

export function generateOrganicShadow(args: {
  color: string;
  steps: number;
  size: number;
  opacity: number;
  opacityTransform?: (progress: number) => number;
  blurTransform?: (progress: number, target: number) => number;
  offsetTransform?: (progress: number, target: number) => number;
  spreadTransform?: (progress: number, target: number) => number;
}): string {
  const {
    blurTransform = defaults.blurTransform,
    offsetTransform = defaults.offsetTransform,
    opacityTransform = defaults.opacityTransform,
    spreadTransform = defaults.spreadTransform,
  } = args;

  return Array.from({ length: args.steps }, (_, i) => {
    const progress = i / (args.steps - 1);
    const blur = blurTransform(progress, args.size);
    const offset = offsetTransform(progress, args.size);
    const opacity = opacityTransform(progress, args.opacity);
    const spread = spreadTransform(progress, args.size);

    return `0 ${offset.toFixed(2)}px ${blur.toFixed(2)}px ${spread.toFixed(2)}px ${rgba(args.color, parseFloat(opacity.toFixed(2)))}`;
    // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- I still don't understand this lint rule
  }).join(", ");
}

export type BoxShadowCss = Record<
  "none" | "sm" | "md" | "lg" | "xl" | "xxl",
  ReturnType<typeof css>
>;

const edgeDark = css`
  background-clip: padding-box;
  border: 1px solid hsl(0 0% 0% / 0.28);
  box-shadow: ${() => {
    const highlight = "inset 0 1px 1px -0.5px hsl(0 0% 100% / 0.05)";
    const edge = `inset 0 0 0 1px ${rgba(darkBorderColor, 0.24)}`;
    const organic = "var(--theme-organic-shadow, 0 0 0)";
    return [edge, highlight, organic].join(", ");
  }};
`;

const edgeLight = css`
  background-clip: padding-box;
  border: 1px solid rgba(11 14 22 / 0.06);
  box-shadow: var(--theme-organic-shadow, 0 0 0);
`;

export const boxShadowCssDark: BoxShadowCss = {
  none: css`
    box-shadow: none;
  `,
  sm: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "#0000",
      steps: 2,
      size: 2,
      opacity: 0.12,
    })};
    ${edgeDark}
  `,
  md: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "#0000",
      steps: 4,
      size: 12,
      opacity: 0.1,
    })};
    ${edgeDark}
  `,
  lg: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "#0000",
      steps: 5,
      size: 24,
      opacity: 0.1,
    })};
    ${edgeDark}
  `,
  xl: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "#0000",
      steps: 5,
      size: 48,
      opacity: 0.11,
    })};
    ${edgeDark}
  `,
  xxl: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "#0000",
      steps: 8,
      size: 80,
      opacity: 0.1,
    })};
    ${edgeDark}
  `,
};

export const boxShadowCssLight: BoxShadowCss = {
  none: css`
    box-shadow: none;
  `,
  sm: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "rgb(11, 14, 22)",
      steps: 2,
      size: 2,
      opacity: 0.09,
    })};
    ${edgeLight}
  `,
  md: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "rgb(11, 14, 22)",
      steps: 4,
      size: 12,
      opacity: 0.06,
    })};
    ${edgeLight}
  `,
  lg: css`
    ${edgeLight}
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "rgb(11, 14, 22)",
      steps: 5,
      size: 24,
      opacity: 0.06,
    })};
  `,
  xl: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "rgb(11, 14, 22)",
      steps: 5,
      size: 48,
      opacity: 0.08,
    })};
    ${edgeLight}
  `,
  xxl: css`
    --theme-organic-shadow: ${generateOrganicShadow({
      color: "rgb(11, 14, 22)",
      steps: 5,
      size: 80,
      opacity: 0.08,
    })};
    ${edgeLight}
  `,
};
